import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { CheckCircle2, Circle, ArrowDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useAgreement() {
  const [agreement, setAgreement] = useState(null);
  useEffect(() => {
    axios.get(`${API}/agreement`).then((r) => setAgreement(r.data)).catch(() => {});
  }, []);
  return agreement;
}

export function SignProgress({ read, acknowledged, named }) {
  const Item = ({ ok, label, testid }) => (
    <span className={`inline-flex items-center gap-1.5 ${ok ? "text-emerald-700" : "text-slate-400"}`} data-testid={testid} data-state={ok ? "done" : "pending"}>
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />} {label}
    </span>
  );
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-tech text-[11px] uppercase tracking-wide" data-testid="sign-progress">
      <Item ok={read} label="Read complete" testid="sign-progress-read" />
      <span className="text-slate-300">·</span>
      <Item ok={acknowledged} label="Acknowledge box" testid="sign-progress-ack" />
      <span className="text-slate-300">·</span>
      <Item ok={named} label="Name entered" testid="sign-progress-name" />
    </div>
  );
}

export default function AgreementReader({ agreement, onReadComplete, className = "h-64" }) {
  const ref = useRef(null);
  const [done, setDone] = useState(false);

  const check = () => {
    const el = ref.current;
    if (!el || done) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 16) {
      setDone(true);
      onReadComplete(true);
    }
  };

  useEffect(() => { if (agreement) setTimeout(check, 50); }, [agreement]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!agreement) return <div className={`${className} border border-slate-200 bg-slate-50 animate-pulse`} data-testid="agreement-loading" />;

  return (
    <div className="relative">
      <div ref={ref} onScroll={check} data-testid="agreement-reader"
        className={`${className} overflow-y-auto border border-slate-200 bg-white p-5 sm:p-6 space-y-5 text-sm leading-relaxed text-slate-700`}>
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900">{agreement.title}</h3>
          <p className="font-tech text-[11px] uppercase tracking-[0.16em] text-slate-500 mt-1" data-testid="agreement-version">Version {agreement.version}</p>
        </div>
        {agreement.sections.map((s) => (
          <section key={s.n} data-testid={`agreement-section-${s.n}`}>
            <h4 className="font-semibold text-slate-900">{s.n}. {s.title}</h4>
            <p className="mt-1">{s.body}</p>
          </section>
        ))}
      </div>
      {!done && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent flex items-end justify-center pb-2">
          <span className="inline-flex items-center gap-1 font-tech text-[10px] uppercase tracking-wide text-slate-500 bg-white/90 px-2 py-0.5 border border-slate-200" data-testid="agreement-scroll-hint">
            <ArrowDown className="h-3 w-3" /> Scroll to read all sections
          </span>
        </div>
      )}
    </div>
  );
}
